export const DOCUMENT_SOURCE_CLASS_NAMES = {
  INTERNAL: 'Внутренний документ бизнеса',
  CTF: 'Справка CTF',
  LIBRARY: 'Библиотека',
  EXTERNAL: 'Внешний источник',
} as const;

export type DocumentSourceClassName = (typeof DOCUMENT_SOURCE_CLASS_NAMES)[keyof typeof DOCUMENT_SOURCE_CLASS_NAMES];

/** Признак библиотеки в имени папки или организации — сравнение регистронезависимое, как на бэкенде. */
const LIBRARY_NAME_SIGNAL = 'библиотека';

/**
 * Признак справки СТФ в имени файла: кириллица, латиница и смешанные написания (С/C, Т/T, Ф/F) в любом регистре.
 * Слева — начало имени или любой неалфавитно-цифровой разделитель (пробел, `_`, `-`, `.`), справа — та же граница,
 * чтобы признак не срабатывал внутри других слов.
 */
const CTF_FILE_NAME_PATTERN = /(?:^|[^\p{L}\p{N}])[СC][ТT][ФF](?![\p{L}\p{N}])/iu;

type DetectDocumentSourceClassParams = {
  fileNames: string[];
  folderNames: string[];
  organizationName?: string | null;
};

export function hasCtfFileNameSignal(fileName: string): boolean {
  return CTF_FILE_NAME_PATTERN.test(fileName);
}

export function hasLibraryOrganizationNameSignal(organizationName: string): boolean {
  return organizationName.toLowerCase().includes(LIBRARY_NAME_SIGNAL);
}

export function hasLibraryFolderNameSignal(folderNames: string[]): boolean {
  return folderNames.some((folderName) => folderName.toLowerCase() === LIBRARY_NAME_SIGNAL);
}

/**
 * Класс источника по приоритету: справка CTF → библиотека → внутренний документ бизнеса.
 * «Внешний источник» автоматически не подставляется — его выбирают вручную.
 * При загрузке нескольких файлов класс CTF подставляется, только если признак есть у всех файлов.
 */
export function detectDocumentSourceClass({
  fileNames,
  folderNames,
  organizationName,
}: DetectDocumentSourceClassParams): DocumentSourceClassName {
  if (fileNames.length > 0 && fileNames.every((fileName) => hasCtfFileNameSignal(fileName))) {
    return DOCUMENT_SOURCE_CLASS_NAMES.CTF;
  }

  const isLibraryOrganization = organizationName ? hasLibraryOrganizationNameSignal(organizationName) : false;

  if (isLibraryOrganization || hasLibraryFolderNameSignal(folderNames)) {
    return DOCUMENT_SOURCE_CLASS_NAMES.LIBRARY;
  }

  return DOCUMENT_SOURCE_CLASS_NAMES.INTERNAL;
}
